import { ApolloClient, Reference } from '@apollo/client';

import type { InteractiveLessonSectionComponentFragment } from './lesson-builder.graphql.generated';
import {
  ActivityComponentProperties,
  ComponentColumn,
} from './components/component.constants';
import type { LessonActivitiesFragment } from './lesson-builder.graphql.generated';
import {
  GET_ACTIVITIES_SECTIONS,
  GET_DRAFT_LESSON_ACTIVITIES,
  interactiveLessonSectionComponentFragment,
  interactiveLessonSectionFragment,
  interactiveLessonSectionOverviewFragment,
  interactiveLessonSubSectionFragment,
} from './lesson-builder.graphql';
import type {
  ComponentDataInput,
  SubActivityDetailsInput,
} from '../../gql/schema-types';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import { SectionOrSubSection } from './activity-builder.constants';
import type {
  GetActivitiesSectionsQuery,
  GetActivitiesSectionsQueryVariables,
} from './lesson-builder.graphql.generated';
import type { GetDraftLessonActivitiesQuery } from './lesson-builder.graphql.generated';
import type { GetDraftAssessmentActivitiesQuery } from './assessment/assessment-builder.graphql.generated';
import {
  LESSON_ACTIVITIES,
  ActivitiesContentType,
  LANGUAGE_CODE_EN_AU,
  ACTIVITIES_CONTENT_STATUS_DRAFT,
  LanguageCodes,
  ASSESSMENT_ACTIVITIES,
  ASSESSMENT,
  LESSON,
} from 'global/constants';
import { GET_DRAFT_ASSESSMENT_ACTIVITIES } from './assessment/assessment-builder.graphql';
import {
  InquisitiveActivitiesContentType,
  InquisitiveActivitiesPageType,
} from 'global/graphql/scalars.type';
import type { InteractiveLessonSectionFragment } from './lesson-builder.graphql.generated';
import { ColumnStyle } from './components/columns/settings/column-settings.constants';
import type { Pluck } from 'lib/gql/pluck';

export function removeComponentFromSection({
  client,
  componentId,
  sectionId,
}: {
  client: ApolloClient<any>;
  componentId: string;
  sectionId: string;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: sectionId,
      __typename: 'ActivitiesSection',
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      components(existingComponentRefs, { readField }) {
        return existingComponentRefs.filter(
          (cRef: Reference) => componentId !== readField('id', cRef)
        );
      },
    },
  });
}

export function addNewComponentToCache({
  client,
  component,
}: {
  client: ApolloClient<any>;
  component: InteractiveLessonSectionComponentFragment;
}) {
  client.writeFragment({
    id: client.cache.identify({
      id: component.id,
      __typename: component.__typename,
      language: LANGUAGE_CODE_EN_AU,
      version: component.version,
    }),
    fragmentName: 'InteractiveLessonSectionComponent',
    fragment: interactiveLessonSectionComponentFragment,
    data: component,
  });
}

export function addComponentToSection({
  client,
  componentId,
  componentTypename,
  sectionId,
}: {
  client: ApolloClient<any>;
  componentId: string;
  componentTypename: string;
  sectionId: string;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: sectionId,
      __typename: 'ActivitiesSection',
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      components(existingComponentRefs) {
        return [
          ...existingComponentRefs,
          {
            __ref: client.cache.identify({
              id: componentId,
              __typename: componentTypename,
              language: LANGUAGE_CODE_EN_AU,
              version: ACTIVITIES_CONTENT_STATUS_DRAFT,
            }),
          },
        ];
      },
    },
  });
}

export function removeComponentFromColumn({
  client,
  componentId,
  column,
}: {
  client: ApolloClient<any>;
  componentId: string;
  column: ComponentColumn;
}) {
  client.cache.modify({
    id: client.cache.identify({
      columnId: column.columnId,
      __typename: column.__typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      components(existingComponentRefs, { readField }) {
        return existingComponentRefs.filter(
          (cRef: Reference) => componentId !== readField('id', cRef)
        );
      },
    },
  });
}

export function addComponentToColumn({
  client,
  component,
  column,
}: {
  client: ApolloClient<any>;
  component: InteractiveLessonSectionComponentFragment;
  column: ComponentColumn;
}) {
  const identifyComponent = client.cache.identify({
    id: component.id,
    __typename: component.__typename,
    language: LANGUAGE_CODE_EN_AU,
    version: ACTIVITIES_CONTENT_STATUS_DRAFT,
  });
  client.cache.modify({
    id: client.cache.identify({
      columnId: column.columnId,
      __typename: column.__typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      components(existingComponentRefs) {
        return [
          ...existingComponentRefs,
          {
            __ref: identifyComponent,
          },
        ];
      },
    },
  });
}

export function reorderComponent({
  client,
  id,
  typename,
  sort,
}: {
  client: ApolloClient<any>;
  id: string;
  typename: string;
  sort: string;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id,
      __typename: typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      sort() {
        return sort;
      },
    },
  });
}

export function deleteComponentFromCache({
  client,
  id,
  typename,
}: {
  client: ApolloClient<any>;
  id: string;
  typename: string;
}) {
  client.cache.evict({
    id: client.cache.identify({
      id,
      __typename: typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
  });
  client.cache.gc();
}

export function upsertSectionOverview({
  client,
  sectionOverview,
}: {
  client: ApolloClient<any>;
  sectionOverview: Pluck<LessonActivitiesFragment, 'sectionsOverview'>;
}) {
  client.writeFragment({
    id: client.cache.identify({
      id: sectionOverview.id,
      __typename: sectionOverview.__typename,
    }),
    fragmentName: 'InteractiveLessonSectionOverview',
    fragment: interactiveLessonSectionOverviewFragment,
    data: sectionOverview,
  });
}

export function modifyPageTypeInCache({
  client,
  id,
  pageType,
}: {
  client: ApolloClient<any>;
  id: string;
  pageType: InquisitiveActivitiesPageType;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id,
      language: LANGUAGE_CODE_EN_AU,
      __typename: 'ActivitiesSection',
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      pageType: existing => {
        return pageType;
      },
    },
  });
}

export function writeSectionQueryById({
  client,
  data,
  contentId,
  contentType,
  sectionId,
}: {
  client: ApolloClient<any>;
  data: Pluck<GetActivitiesSectionsQuery, 'getActivitiesSections'>;
  contentId: number;
  contentType: ActivitiesContentType;
  sectionId: string;
}) {
  client.writeQuery<
    GetActivitiesSectionsQuery,
    GetActivitiesSectionsQueryVariables
  >({
    query: GET_ACTIVITIES_SECTIONS,
    data: {
      getActivitiesSections: [data],
    },
    variables: {
      input: {
        contentId,
        contentType,
        sectionsIds: [sectionId],
        version: ACTIVITIES_CONTENT_STATUS_DRAFT,
      },
    },
  });
}

export function addSectionOverviewToLesson({
  client,
  contentId,
  sectionId,
  contentType,
}: {
  client: ApolloClient<any>;
  contentId: number;
  sectionId: string;
  contentType: ActivitiesContentType;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: contentId,
      language: LANGUAGE_CODE_EN_AU,
      __typename:
        contentType === LESSON_ACTIVITIES
          ? 'LessonActivities'
          : 'AssessmentActivities',
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      sectionsOverview(existingRefs) {
        return [
          ...existingRefs,
          {
            __ref: client.cache.identify({
              id: sectionId,
              __typename: 'ActivitiesSectionOverview',
              version: ACTIVITIES_CONTENT_STATUS_DRAFT,
            }),
          },
        ];
      },
    },
  });
}

export function removeSectionOverviewFromLesson({
  client,
  contentId,
  sectionId,
  contentType,
}: {
  client: ApolloClient<any>;
  contentId: number;
  sectionId: string;
  contentType: ActivitiesContentType;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: contentId,
      language: LANGUAGE_CODE_EN_AU,
      __typename:
        contentType === LESSON_ACTIVITIES
          ? 'LessonActivities'
          : 'AssessmentActivities',
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      sectionsOverview(existingRefs) {
        return [
          ...existingRefs.filter(
            (r: Reference) =>
              r.__ref !==
              client.cache.identify({
                id: sectionId,
                __typename: 'ActivitiesSectionOverview',
                version: ACTIVITIES_CONTENT_STATUS_DRAFT,
              })
          ),
        ];
      },
    },
  });
  client.cache.gc();
}

export function updateExistingComponent({
  client,
  id,
  typename,
  componentData,
}: {
  client: ApolloClient<any>;
  id: string;
  typename: string;
  componentData: ComponentDataInput;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id,
      __typename: typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      properties(existing) {
        return { ...existing, ...componentData.properties };
      },
    },
  });
}

export function updateExistingComponentProperties({
  client,
  id,
  typename,
  properties,
}: {
  client: ApolloClient<any>;
  id: string;
  typename: string;
  properties: ActivityComponentProperties;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id,
      __typename: typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      properties(existing) {
        return { ...existing, ...properties };
      },
    },
  });
}

export function updateExistingColumnComponent({
  client,
  id,
  typename,
  data,
}: {
  client: ApolloClient<any>;
  id: string;
  typename: string;
  data: {
    index: number;
    columnId: string;
    colspan: number;
    alignment: string;
    style: Omit<ColumnStyle, '__typename'> | null;
  };
}) {
  client.cache.modify({
    id: client.cache.identify({
      columnId: id,
      __typename: typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      index: () => data.index,
      alignment: () => data.alignment,
      colspan: () => data.colspan,
      style(existing) {
        return data.style;
      },
    },
  });
}

export function updateSubActivityProperties({
  client,
  id,
  typename,
  properties,
}: {
  client: ApolloClient<any>;
  id: string;
  typename: string;
  properties: SubActivityDetailsInput;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id,
      __typename: typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      subActivityProperties(existing) {
        return {
          ...existing,
          ...properties,
        };
      },
    },
  });
}

export function addSubActivityToActivity({
  client,
  id,
  typename,
  subActivity,
}: {
  client: ApolloClient<any>;
  id: string;
  typename: string;
  subActivity: InteractiveLessonSectionComponentFragment;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id,
      __typename: typename,
      language: LANGUAGE_CODE_EN_AU,
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      subActivities(existingRefs) {
        return [
          ...existingRefs,
          {
            __ref: client.cache.identify({
              id: subActivity.id,
              __typename: subActivity.__typename,
              language: LANGUAGE_CODE_EN_AU,
              version: subActivity.version,
            }),
          },
        ];
      },
    },
  });
}

export function updateSectionOverviewActivityDetails({
  client,
  sectionId,
  differentiation,
  checkPoint,
}: {
  client: ApolloClient<any>;
  sectionId: string;
  differentiation: Differentiation | null;
  checkPoint: boolean | null;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: sectionId,
      __typename: 'ActivitiesSectionOverview',
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      differentiation() {
        return differentiation;
      },
      checkPoint() {
        return checkPoint;
      },
    },
  });
}

export function resetSectionInCache({
  client,
  section,
}: {
  client: ApolloClient<any>;
  section: SectionOrSubSection;
}) {
  if (
    (
      section as Pluck<
        GetActivitiesSectionsQuery,
        'getActivitiesSections.subSections'
      >
    )?.parentSectionId
  ) {
    client.writeFragment({
      id: client.cache.identify({
        id: section.id,
        __typename: section.__typename,
        language: LANGUAGE_CODE_EN_AU,
        version: section.version,
      }),
      fragmentName: 'InteractiveLessonSubSection',
      fragment: interactiveLessonSubSectionFragment,
      data: section,
    });
  } else {
    client.writeFragment({
      id: client.cache.identify({
        id: section.id,
        __typename: section.__typename,
        language: LANGUAGE_CODE_EN_AU,
        version: section.version,
      }),
      fragmentName: 'InteractiveLessonSectionFragment',
      fragment: interactiveLessonSectionFragment,
      data: section,
    });
  }
}

export function resetComponentInCache({
  client,
  component,
}: {
  client: ApolloClient<any>;
  component: Pluck<InteractiveLessonSectionFragment, 'components'>;
}) {
  client.writeFragment<Pluck<InteractiveLessonSectionFragment, 'components'>>({
    id: client.cache.identify({
      __typename: component.__typename,
      id: component.id,
      language: LANGUAGE_CODE_EN_AU,
      version: component.version,
    }),
    fragment: interactiveLessonSectionComponentFragment,
    fragmentName: 'InteractiveLessonSectionComponent',
    data: component,
  });
}

export function resetInteractiveContentInCache({
  client,
  contentType,
  classId,
  interactiveContent,
}: {
  client: ApolloClient<any>;
  contentType: ActivitiesContentType;
  classId: number;
  interactiveContent?:
    | Pluck<GetDraftLessonActivitiesQuery, 'getDraftLessonActivities'>
    | Pluck<GetDraftAssessmentActivitiesQuery, 'getDraftAssessmentActivities'>
    | null;
}) {
  if (contentType === LESSON_ACTIVITIES) {
    client.writeQuery({
      query: GET_DRAFT_LESSON_ACTIVITIES,
      data: {
        getDraftLessonActivities: interactiveContent,
      },
      variables: {
        input: {
          contentId: interactiveContent?.id,
          classId,
        },
      },
    });
  } else {
    client.writeQuery({
      query: GET_DRAFT_ASSESSMENT_ACTIVITIES,
      data: {
        getDraftAssessmentActivities: interactiveContent,
      },
      variables: {
        input: {
          contentId: interactiveContent?.id,
          classId,
        },
      },
    });
  }
}

export const clearActiveVersionFromCache = ({
  client,
  contentId,
  contentType,
}: {
  client: ApolloClient<any>;
  contentId: number;
  contentType: ActivitiesContentType;
}) => {
  client.cache.evict({
    id: client.cache.identify({
      id: contentId,
      language: LANGUAGE_CODE_EN_AU,
      __typename:
        contentType === LESSON_ACTIVITIES
          ? 'LessonActivities'
          : 'AssessmentActivities',
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
  });
  client.cache.gc();
};

export const clearActiveSectionsFromCache = ({
  client,
  sectionIds,
}: {
  client: ApolloClient<any>;
  sectionIds: string[];
}) => {
  // remove query from cache so it reruns after clearing the sections
  client.cache.evict({
    id: 'ROOT_QUERY',
    fieldName: 'getActivitiesSections',
  });
  sectionIds.forEach(sectionId => {
    client.cache.evict({
      id: client.cache.identify({
        id: sectionId,
        __typename: 'ActivitiesSection',
        language: LANGUAGE_CODE_EN_AU,
        version: null,
      }),
    });
  });
  client.cache.gc();
};

export function modifySubSectionOverviewSort({
  client,
  id,
  sort,
}: {
  client: ApolloClient<any>;
  id: string;
  sort: string;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id,
      __typename: 'ActivitiesSectionOverview',
      version: null,
    }),
    fields: {
      sort: () => sort,
    },
  });
}

export function modifyActivitiesPublishChanges({
  client,
  id,
  contentType,
}: {
  client: ApolloClient<any>;
  id: number;
  contentType: InquisitiveActivitiesContentType;
}) {
  client.cache.modify({
    id: client.cache.identify({
      id: id,
      language: LANGUAGE_CODE_EN_AU,
      __typename:
        contentType === LESSON_ACTIVITIES
          ? 'LessonActivities'
          : 'AssessmentActivities',
      version: ACTIVITIES_CONTENT_STATUS_DRAFT,
    }),
    fields: {
      hasUnpublishedChanges: () => true,
    },
  });
}

export function updateIsAssignedInCache({
  client,
  id,
  contentType,
  isAssigned,
  language,
  version,
}: {
  client: ApolloClient<any>;
  id: number;
  contentType: string;
  isAssigned: boolean;
  language: LanguageCodes;
  version: string;
}) {
  const isAssessment = contentType === ASSESSMENT_ACTIVITIES;
  const activityTypeName = isAssessment
    ? 'AssessmentActivities'
    : 'LessonActivities';
  const property = isAssessment ? ASSESSMENT : LESSON;

  client.cache.modify({
    id: client.cache.identify({
      id,
      language,
      __typename: activityTypeName,
      version,
    }),
    fields: {
      [property]: existing => ({
        ...existing,
        isAssigned,
      }),
    },
  });
}
